import lodashGet from "lodash/get";
import React from "react";
import { twMerge } from "tailwind-merge";
import PillarTableCellContent from "shared/components/pillar-table/cell-content/PillarTableCellContent";
import PillarTableColumn from "shared/components/pillar-table/columns/PillarTableColumn";
import PillarTableColumnCheckbox from "shared/components/pillar-table/columns/PillarTableColumnCheckbox";
import { findPillarTableColumnComponent } from "shared/components/pillar-table/columns/utils/findPillarTableColumnComponent";
import PillarTableRowActionError from "shared/components/pillar-table/error/PillarTableRowActionError";
import { getFixedColumnStyle } from "shared/components/pillar-table/helpers/addFixedStyleToColumn";
import { getColumnStyles } from "shared/components/pillar-table/helpers/getColumnStyles";
import PillarTableNestedTableComponentWrapper from "shared/components/pillar-table/nested/PillarTableNestedTableComponentWrapper";
import { filterPillarTableDataColumns } from "shared/components/pillar-table/nested/pillarTableNestedTableHelpers";
import PillarTableNestedToggle from "shared/components/pillar-table/nested/PillarTableNestedToggle";
import { usePillarTableContext } from "shared/components/pillar-table/PillarTableContext";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import PillarTableRowInlineDelete from "shared/components/pillar-table/row/PillarTableRowInlineDelete";
import PillarTableRowInlineEdit from "shared/components/pillar-table/row/PillarTableRowInlineEdit";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";

export interface PillarTableRowProps<RowGeneric = PillarTableSafeAny> {
  rowIndex: number;
  rowValue: RowGeneric;
}

const PillarTableRow = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>() => {
  const { testidTable, handleRowClick, fixedColumnWidths, isInfinityTable } =
    usePillarTableContext();

  const {
    rowIndex,
    rowValue,
    setRowExpanded,
    inlineEditMode,
    virtualRowValue,
  } = usePillarTableRowContext();

  const isVirtualRow =
    isInfinityTable && !virtualRowValue?.isColumnWidthReferenceRow;
  const isColumnWidthReferenceRow =
    isInfinityTable && !!virtualRowValue?.isColumnWidthReferenceRow;

  return (
    <>
      <tr
        data-testid={`${testidTable}-table-row-${rowIndex}${virtualRowValue?.isColumnWidthReferenceRow ? "-reference" : ""}`}
        data-index={isVirtualRow ? virtualRowValue?.index : undefined}
        key={isVirtualRow ? virtualRowValue?.key : `row-${rowIndex}`}
        ref={isVirtualRow ? virtualRowValue?.ref : undefined}
        className={twMerge(
          rowIndex % 2 ? "bg-neutral-light-500" : "bg-neutral-light",
          handleRowClick && inlineEditMode === "none"
            ? "pillartable-table-row-interactable group"
            : "pillartable-table-row-general",
          isVirtualRow &&
            "absolute top-0 inset-x-0 w-full translate-y-[var(--row-offset)]",
          isColumnWidthReferenceRow && "invisible pointer-events-none",
        )}
        style={
          isVirtualRow
            ? {
                ["--row-offset" as string]: `${virtualRowValue?.offset}px`,
                ...(fixedColumnWidths.length > 0 &&
                fixedColumnWidths.some((width) => width > 0)
                  ? {
                      display: "grid",
                      gridTemplateColumns: fixedColumnWidths
                        .map((width) => `${width}px`)
                        .join(" "),
                    }
                  : {}),
              }
            : undefined
        }
        onClick={() => {
          if (handleRowClick && inlineEditMode === "none") {
            handleRowClick(rowValue);
          } else {
            setRowExpanded((prev) => {
              if (prev === "open") {
                return "close";
              }
              if (prev === "close") {
                return "open";
              }
              return "none";
            });
          }
        }}
      >
        <PillarTableRowInner<
          RowGeneric,
          ExtraPropsGeneric,
          InlineEditFormGeneric
        > />
      </tr>
      <PillarTableNestedTableComponentWrapper />
    </>
  );
};

// Using this inner component to ensure our useContext has the proper updated values from
// the root parent component. Without inner component the useContext only returns with the
// default values and does not update.
const PillarTableRowInner = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>() => {
  const router = useCustomRouter();
  const session = useCustomSession();
  const { rowIndex, inlineEditMode, rowValue, rowActionFailure } =
    usePillarTableRowContext<
      RowGeneric,
      ExtraPropsGeneric,
      InlineEditFormGeneric
    >();
  const { testidTable, fixedColumnWidths, columns } = usePillarTableContext<
    RowGeneric,
    ExtraPropsGeneric
  >();
  const tableColumns = filterPillarTableDataColumns(columns);

  if (!tableColumns || tableColumns.length === 0) {
    return <></>;
  }
  return (
    <>
      {tableColumns.map((column, columnIndex: number) => {
        const actualColumnType = findPillarTableColumnComponent(column.type);

        if (
          actualColumnType &&
          "isEditComponent" in actualColumnType &&
          actualColumnType.isEditComponent &&
          inlineEditMode !== "edit"
        ) {
          const parentType = findFirstParent(column.type);
          if (parentType) {
            column = { ...column, type: parentType };
          }
        }

        return (
          <td
            data-testid={
              testidTable && column.props.testid
                ? `${testidTable}-table-body-row-column-${column.props.testid}`
                : undefined
            }
            key={`row-${rowIndex}-column-${columnIndex}`}
            className={`whitespace-nowrap ${
              column.props.linkTo ||
              column.props.linkToAdmin ||
              column.props.linkToHub
                ? "pillartable-table-cell-interactable"
                : "pillartable-table-cell-general"
            } ${column.props.className ?? ""}`}
            style={{
              ...getFixedColumnStyle({
                column,
                index: columnIndex,
                fixedColumnWidths: fixedColumnWidths || [],
                customStyles: "hsl(210, 15%, 100%)",
              }),
              ...getColumnStyles(column),
            }}
            onClick={(e) => {
              if (column.type === PillarTableColumnCheckbox) {
                e.stopPropagation();
              }
              const combinedData = { society: session?.society, ...rowValue };
              if (
                column.props.linkTo &&
                typeof column.props.linkTo === "function"
              ) {
                column.props.linkTo();
                e.stopPropagation();
              } else if (
                typeof column.props.linkTo &&
                typeof column.props.linkTo === "object"
              ) {
                Object.keys(column.props.linkTo.params || {}).forEach((key) => {
                  column.props.linkTo.path = column.props.linkTo.path.replace(
                    `[${key}]`,
                    lodashGet(combinedData, column.props.linkTo.params[key]),
                  );
                });
                router.push(column.props.linkTo.path);
                e.stopPropagation();
              } else if (
                column.props.linkToAdmin &&
                typeof column.props.linkToAdmin === "function"
              ) {
                column.props.linkToAdmin();
                e.stopPropagation();
              } else if (
                column.props.linkToAdmin &&
                typeof column.props.linkToAdmin === "object"
              ) {
                Object.keys(column.props.linkToAdmin.params || {}).forEach(
                  (key) => {
                    column.props.linkToAdmin.path =
                      column.props.linkToAdmin.path.replace(
                        `[${key}]`,
                        lodashGet(
                          combinedData,
                          column.props.linkToAdmin.params[key],
                        ),
                      );
                  },
                );
                router.push(column.props.linkToAdmin.path);
                e.stopPropagation();
              }
            }}
          >
            {columnIndex === 0 && <PillarTableNestedToggle />}
            {column.type == PillarTableColumn ? (
              <div
                className={`${
                  column.props.contentClassName
                    ? column.props.contentClassName
                    : "flex gap-x-1 break-words overflow-visible whitespace-normal"
                }`}
              >
                <PillarTableCellContent<RowGeneric>
                  {...column.props}
                  columnIndex={columnIndex}
                />
              </div>
            ) : (
              column
            )}
          </td>
        );
      })}
      {rowActionFailure ? (
        <PillarTableRowActionError />
      ) : (
        <>
          <PillarTableRowInlineEdit />
          <PillarTableRowInlineDelete />
        </>
      )}
    </>
  );
};

export default PillarTableRow;

// Finds the nearest parent in the PillarTableColumn hierarchy
const findFirstParent = (
  // the component you’re looking up
  componentType: any,
  // where to start the search (defaults to PillarTableColumn)
  searchObject: any = PillarTableColumn,
): any | null => {
  // Special handling for nested components like PillarTableColumnText.EditSelectMenu
  // Check if componentType is a nested property of any direct child of searchObject
  for (const key in searchObject) {
    if (
      Object.prototype.hasOwnProperty.call(searchObject, key) &&
      typeof searchObject[key] === "function"
    ) {
      // Check if this function has nested properties that match our componentType
      for (const nestedKey in searchObject[key]) {
        if (
          Object.prototype.hasOwnProperty.call(searchObject[key], nestedKey) &&
          searchObject[key][nestedKey] === componentType
        ) {
          return searchObject[key];
        }
      }
    }
  }

  // Scan direct children of this level
  for (const key in searchObject) {
    if (
      Object.prototype.hasOwnProperty.call(searchObject, key) &&
      typeof searchObject[key] === "function"
    ) {
      // If this property _is_ your component, return its parent (searchObject)
      if (searchObject[key] === componentType) {
        return searchObject;
      }
    }
  }

  // Otherwise, recurse into each child to keep searching
  for (const key in searchObject) {
    if (
      Object.prototype.hasOwnProperty.call(searchObject, key) &&
      typeof searchObject[key] === "function"
    ) {
      const parent = findFirstParent(componentType, searchObject[key]);
      if (parent) {
        return parent;
      }
    }
  }

  // Not found in this branch
  return null;
};
